import cx from "classnames";
import { Location } from "history";
import range from "lodash-es/range";
import React from "react";

import BlogPost from "@/mk2/apps/blogs/components/BlogPost/BlogPost";
import { CommentForumPlaceholder } from "@/mk2/apps/blogs/components/Comment";
import { FeedItemLikedProfile } from "@/mk2/apps/blogs/components/FeedItemLikedProfile";
import HashtagsBox, { BoxedHashtags } from "@/mk2/apps/blogs/components/HashtagsBox";
import { BlogsFeedCategory } from "@/mk2/apps/blogs/containers/BlogsFeed/BlogsFeedPage";
import { getBlogsFeedUrlWithHashtagLink } from "@/mk2/apps/blogs/utils";
import { UserChip } from "@/mk2/apps/forum/components/UserChip";
import { PageRef } from "@/mk2/apps/forum/schemas";
import GroupGridItem from "@/mk2/apps/groups/components/GroupGridItem";
import { Pager } from "@/mk2/components/Pager";
import { AdSlot } from "@/mk2/containers/AdSlot/AdSlot";
import Complaint from "@/mk2/containers/Complaint/Complaint";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Deletion from "@/mk2/containers/Deletion/Deletion";
import { DeletionTargetType } from "@/mk2/containers/Deletion/Deletion.schemas";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { UserAgentParsed } from "@/mk2/helpers/detects";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    BlogFeedItemEntity,
    BlogFeedItemType,
    BlogPostEntity,
    PostVisibility,
    UserEntity,
} from "@/mk2/schemas";
import {
    HistoryLocationWithState,
    RouteNavigateFunction,
    RoutePathPattern,
} from "@/mk2/services/browserHistory";
import {
    FORUM_ARCHIVE_CAMPAIGNS_PAGE_TITLE,
    LIKES_MESSAGE,
    LIKES_PROFILE,
    USER_JOINS_THE_GROUP,
} from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import styles from "./BlogFeedPostsWithPager.mscss";

type PageItemType =
    | "adslot1"
    | "adslot2"
    | "adslot3"
    | "adslot4"
    | "hashtags"
    | "post"
    | "feeditem";

const FEED_PAGE_LENGTH = 25; // == RADIO_FEED_PAGE_LENGTH

interface PageItem {
    type: PageItemType;
    item?: any;
    commentsCount?: number;
    complaintsCount?: number;
    postSubtreeComplaintIds?: number[];
    isComplaintOpened?: boolean;
}

interface OwnProps {
    brandDaysPostId?: number;
    category: BlogsFeedCategory;
    commentsCounts: { [postId: number]: number };
    complaintsCounts: { [postId: number]: number };
    complaintsOpened: boolean[];
    disablePostAuthorBadges?: boolean;
    errorMessage: string;
    feedItems?: BlogFeedItemEntity[];
    hashtags?: BoxedHashtags;
    routeNavigate: RouteNavigateFunction;
    imageComplaintsCounts?: { [imageId: number]: number };
    isMobile: boolean;
    loadingState: LoadingState;
    location: Location;
    routePathPattern: RoutePathPattern;
    membershipsInGroups?: number[];
    numberOfPages: number;
    pageRef: PageRef;
    permissions: string[];
    posts?: BlogPostEntity[];
    postsSubtreeComplaintList?: { [postId: number]: number[] };
    requestUser: UserEntity;
    showHashtagsBox: boolean;
    showStrips: boolean;
    userAgent: UserAgentParsed;

    onDelete?(postId: number);
    onIgnoreUser?(username: string);
    onBlockUser?(userId: number, username: string);
    onPostToggleMenuMore(postId: number);
    onPostToggleVisibilityMenuMore(postId: number);
    onReport(entity: ComplaintTargetType, id: number, location: HistoryLocationWithState);
    onSetDisplayingAtFeed(postId: number, displaying: boolean);
    onSetShowInMagazine?(postId: number, showInMagazine: boolean);
    onSetVisibility?(postId: number, visibility: PostVisibility);
}

type Props = OwnProps;

class BlogFeedPostsWithPager extends React.PureComponent<Props> {
    public render() {
        const { feedItems, loadingState, posts } = this.props;

        return (
            <LoadingSwitch
                loadingState={loadingState}
                onLoad={this.dummyLoad}
                onRenderInit={this.renderInit}
                onRenderSuccess={this.renderSuccess}
                hasData={(posts && posts.length > 0) || (feedItems && feedItems.length > 0)}
            />
        );
    }

    private renderInit = () => {
        const { isMobile, numberOfPages, pageRef, posts, routeNavigate, location } = this.props;

        return (
            <div>
                <span id="topPager" />

                {
                    // vykreslit dostatok elementov, tak aby browser nezmensil scrollTop position
                    // pocas LoadingState.LOADING pretoze vyska body je mensia nez scrollTop pozicia
                    // ktoru sa snazime zachovat
                    range(10).map((i) => (
                        <CommentForumPlaceholder
                            key={`placeholder-${i}`}
                            className={cx(
                                styles.BlogFeedPostsWithPager__commentPlaceholder,
                                i === 0 &&
                                    styles[
                                        "BlogFeedPostsWithPager__commentPlaceholder--hasAbove-Post"
                                    ],
                            )}
                        />
                    ))
                }

                <Pager
                    id="bottomPager"
                    className={cx(
                        styles.BlogFeedPostsWithPager__pager,
                        styles["BlogFeedPostsWithPager__pager--bottom"],
                    )}
                    page={pageRef as number}
                    numberOfPages={numberOfPages}
                    disableHasNext={posts.length < FEED_PAGE_LENGTH}
                    position="bottom"
                    location={location}
                    routeNavigate={routeNavigate}
                    isMobile={isMobile}
                />
            </div>
        );
    };

    private renderSuccess = () => {
        const {
            feedItems,
            hashtags,
            location,
            isMobile,
            numberOfPages,
            pageRef,
            posts,
            routeNavigate,
        } = this.props;

        const hasPager = true;
        const pageItems = this.getPageItems();

        if ((!posts || posts.length === 0) && (!feedItems || feedItems.length === 0)) {
            return null;
        }

        return (
            <React.Fragment>
                <span id="topPager" />

                {pageItems.map((pageItem, idx) => {
                    return pageItem.type === "post" ? (
                        this.renderPostPageItem(pageItem, idx, idx < 2)
                    ) : pageItem.type === "feeditem" ? (
                        this.renderFeedItemPageItem(pageItem, idx, idx < 2)
                    ) : pageItem.type === "hashtags" ? (
                        <HashtagsBox
                            key={idx}
                            hashtags={hashtags}
                            createLink={this.hashtagLinkCreate}
                        />
                    ) : pageItem.type === "adslot1" ? (
                        isMobile ? (
                            <AdSlot
                                key={idx}
                                zone="Blogs Feed - Mid 01 - Mobile"
                                location={location}
                                marginTopWithAd={10}
                            />
                        ) : (
                            <AdSlot
                                key={idx}
                                zone="Blogs Feed - Mid 01 - Desktop"
                                location={location}
                                marginTopWithAd={10}
                            />
                        )
                    ) : pageItem.type === "adslot2" ? (
                        isMobile ? (
                            <AdSlot
                                key={idx}
                                zone="Blogs Feed - Mid 02 - Mobile"
                                location={location}
                                marginTopWithAd={10}
                            />
                        ) : (
                            <AdSlot
                                key={idx}
                                zone="Blogs Feed - Mid 02 - Desktop"
                                location={location}
                                marginTopWithAd={10}
                            />
                        )
                    ) : null;
                })}

                {hasPager && (
                    <Pager
                        id="bottomPager"
                        className={cx(
                            styles.BlogFeedPostsWithPager__pager,
                            styles["BlogFeedPostsWithPager__pager--bottom"],
                        )}
                        page={pageRef as number}
                        numberOfPages={numberOfPages}
                        position="bottom"
                        location={location}
                        routeNavigate={routeNavigate}
                        isMobile={isMobile}
                    />
                )}
            </React.Fragment>
        );
    };

    private renderPostPageItem = (pageItem: PageItem, idx: number, forceEagerImages: boolean) => {
        const {
            brandDaysPostId,
            category,
            disablePostAuthorBadges,
            imageComplaintsCounts,
            isMobile,
            location,
            routeNavigate,
            routePathPattern,
            onDelete,
            onIgnoreUser,
            onBlockUser,
            onPostToggleMenuMore,
            onPostToggleVisibilityMenuMore,
            onReport,
            onSetDisplayingAtFeed,
            onSetShowInMagazine,
            onSetVisibility,
            permissions,
            requestUser,
            showStrips,
            userAgent,
        } = this.props;

        const post = pageItem.item as BlogPostEntity;

        return (
            <div
                key={idx}
                className={cx(
                    styles.BlogFeedPostsWithPager__post,
                    pageItem.isComplaintOpened &&
                        styles["BlogFeedPostsWithPager__post--complaintOpened"],
                )}
            >
                <Complaint
                    id={post.id}
                    entity={ComplaintTargetType.PhotoblogPost}
                    className={styles.BlogFeedPostsWithPager__post__complaint}
                    scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                    inForum={false}
                    inGroups={false}
                />

                <Deletion
                    id={post.id}
                    entity={DeletionTargetType.PhotoblogPost}
                    className={styles.BlogFeedPostsWithPager__post__complaint}
                    scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                />

                {brandDaysPostId && post.id === brandDaysPostId && (
                    <div className={styles.BlogFeedPostsWithPager__post__brandDaysPostLabel}>
                        {FORUM_ARCHIVE_CAMPAIGNS_PAGE_TITLE}
                    </div>
                )}

                <BlogPost
                    className={cx(
                        brandDaysPostId &&
                            post.id === brandDaysPostId &&
                            styles["BlogFeedPostsWithPager__post__blogPost--brandDaysPost"],
                        pageItem.isComplaintOpened &&
                            styles["BlogFeedPostsWithPager__post__blogPost--complaintOpened"],
                    )}
                    commentsCount={pageItem.commentsCount}
                    complaintsCount={pageItem.complaintsCount}
                    disablePostAuthorBadges={disablePostAuthorBadges}
                    forceEagerImages={forceEagerImages}
                    imageComplaintsCounts={imageComplaintsCounts}
                    isMobile={isMobile}
                    location={location}
                    routeNavigate={routeNavigate}
                    routePathPattern={routePathPattern}
                    onDelete={onDelete}
                    onIgnoreUser={
                        brandDaysPostId && post.id === brandDaysPostId ? null : onIgnoreUser
                    }
                    onBlockUser={onBlockUser}
                    onReport={onReport}
                    onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                    onSetShowInMagazine={onSetShowInMagazine}
                    onSetVisibility={onSetVisibility}
                    onToggleMenuMore={onPostToggleMenuMore}
                    onToggleVisibilityMenuMore={onPostToggleVisibilityMenuMore}
                    post={post}
                    postSubtreeComplaintIds={pageItem.postSubtreeComplaintIds}
                    requestUser={requestUser}
                    requestUserPermissions={permissions}
                    showGroup={category === BlogsFeedCategory.FLAGGED_GROUPS}
                    showStrip={showStrips}
                    userAgent={userAgent}
                />
            </div>
        );
    };

    private renderFeedItemPageItem = (
        pageItem: PageItem,
        idx: number,
        forceEagerImages: boolean,
    ) => {
        const {
            imageComplaintsCounts,
            isMobile,
            location,
            routeNavigate,
            routePathPattern,
            membershipsInGroups,
            onDelete,
            onIgnoreUser,
            onBlockUser,
            onPostToggleMenuMore,
            onPostToggleVisibilityMenuMore,
            onReport,
            onSetDisplayingAtFeed,
            onSetShowInMagazine,
            onSetVisibility,
            permissions,
            requestUser,
            showStrips,
            userAgent,
        } = this.props;

        const feedItem = pageItem.item as BlogFeedItemEntity;
        const hasFeedItemHeadline = feedItem.itemType !== BlogFeedItemType.POST;
        return (
            <div
                key={idx}
                className={cx(
                    styles.BlogFeedPostsWithPager__post,
                    pageItem.isComplaintOpened &&
                        styles["BlogFeedPostsWithPager__post--complaintOpened"],
                )}
            >
                {feedItem.post && (
                    <Complaint
                        id={feedItem.post.id}
                        entity={ComplaintTargetType.PhotoblogPost}
                        className={styles.BlogFeedPostsWithPager__post__complaint}
                        scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                        inForum={false}
                        inGroups={false}
                    />
                )}

                {feedItem.post && (
                    <Deletion
                        id={feedItem.id}
                        entity={DeletionTargetType.PhotoblogPost}
                        className={styles.BlogFeedPostsWithPager__post__complaint}
                        scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                    />
                )}

                {feedItem.itemType === BlogFeedItemType.POST_LIKE && (
                    <div
                        className={cx(
                            styles.BlogFeedPostsWithPager__post__feedItemHeadline,
                            pageItem.isComplaintOpened &&
                                styles["BlogFeedPostsWithPager__post--squareTopCorners"],
                        )}
                    >
                        <UserChip user={feedItem.user} size={24} spaceBetween={4} boldUsername />
                        &nbsp;
                        <span className="actor-action">{LIKES_MESSAGE}</span>
                    </div>
                )}

                {feedItem.itemType === BlogFeedItemType.PROFILE_LIKE && (
                    <div
                        className={cx(
                            styles.BlogFeedPostsWithPager__post__feedItemHeadline,
                            pageItem.isComplaintOpened &&
                                styles["BlogFeedPostsWithPager__post--squareTopCorners"],
                        )}
                    >
                        <UserChip user={feedItem.user} size={24} spaceBetween={4} boldUsername />
                        &nbsp;
                        <span className="actor-action">{LIKES_PROFILE}</span>
                    </div>
                )}

                {feedItem.itemType === BlogFeedItemType.GROUP_MEMBER && (
                    <div
                        className={cx(
                            styles.BlogFeedPostsWithPager__post__feedItemHeadline,
                            pageItem.isComplaintOpened &&
                                styles["BlogFeedPostsWithPager__post--squareTopCorners"],
                        )}
                    >
                        <UserChip user={feedItem.user} size={24} spaceBetween={4} boldUsername />
                        &nbsp;
                        <span className="actor-action">{USER_JOINS_THE_GROUP}</span>
                    </div>
                )}

                {feedItem.groupMember && feedItem.group && (
                    <div>
                        <GroupGridItem
                            className={styles.BlogFeedPostsWithPager__post__feedItemTargetWrapper}
                            activeMembersCount={feedItem.group.activeMembersCount}
                            coverPhoto={feedItem.group.coverPhoto}
                            description={feedItem.group.description}
                            id={feedItem.group.id}
                            isMember={membershipsInGroups.includes(feedItem.group.id)}
                            location={location}
                            locked={feedItem.group.locked}
                            title={feedItem.group.name}
                            onJoinGroup={null}
                            requestUser={requestUser}
                            horizontal
                        />
                    </div>
                )}

                {feedItem.profile && (
                    <div>
                        <FeedItemLikedProfile
                            className={styles.BlogFeedPostsWithPager__post__feedItemTargetWrapper}
                            user={feedItem.profile}
                            userDomainData={feedItem.profileUserDomainData}
                        />
                    </div>
                )}

                {feedItem.post && (
                    <BlogPost
                        className={cx(
                            (pageItem.isComplaintOpened || hasFeedItemHeadline) &&
                                styles["BlogFeedPostsWithPager__post--squareTopCorners"],
                            pageItem.isComplaintOpened &&
                                styles["BlogFeedPostsWithPager__post__blogPost--complaintOpened"],
                        )}
                        commentsCount={pageItem.commentsCount}
                        complaintsCount={pageItem.complaintsCount}
                        forceEagerImages={forceEagerImages}
                        imageComplaintsCounts={imageComplaintsCounts}
                        isMobile={isMobile}
                        location={location}
                        routeNavigate={routeNavigate}
                        routePathPattern={routePathPattern}
                        onDelete={onDelete}
                        onIgnoreUser={onIgnoreUser}
                        onBlockUser={onBlockUser}
                        onReport={onReport}
                        onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                        onSetShowInMagazine={onSetShowInMagazine}
                        onSetVisibility={onSetVisibility}
                        onToggleMenuMore={onPostToggleMenuMore}
                        onToggleVisibilityMenuMore={onPostToggleVisibilityMenuMore}
                        parentFeedItem={feedItem}
                        post={feedItem.post}
                        requestUser={requestUser}
                        requestUserPermissions={permissions}
                        showStrip={showStrips}
                        userAgent={userAgent}
                    />
                )}
            </div>
        );
    };

    private dummyLoad = () => {
        return null;
    };

    private getPageItems = (): PageItem[] => {
        const ADSLOT_INDEXES = [2, 14];
        const HASHTAGS_BOX_INDEX = 6;

        const {
            commentsCounts,
            complaintsCounts,
            complaintsOpened,
            feedItems,
            hashtags,
            posts,
            postsSubtreeComplaintList,
            showHashtagsBox,
            requestUser,
        } = this.props;

        if (posts && feedItems) {
            throw new Error("define posts or feedItems, but not both");
        }

        const items: PageItem[] = posts
            ? posts.map(
                  (post, idx): PageItem => ({
                      type: "post",
                      item: post,
                      commentsCount: commentsCounts[post.id],
                      complaintsCount: complaintsCounts[post.id],
                      postSubtreeComplaintIds: postsSubtreeComplaintList
                          ? postsSubtreeComplaintList[post.id]
                          : null,
                      isComplaintOpened: complaintsOpened[idx],
                  }),
              )
            : feedItems.map(
                  (feedItem, idx): PageItem => ({
                      type: "feeditem",
                      item: feedItem,
                      commentsCount: feedItem.post ? commentsCounts[feedItem.post.id] : 0,
                      complaintsCount: feedItem.post ? complaintsCounts[feedItem.post.id] : 0,
                      postSubtreeComplaintIds:
                          feedItem.post && postsSubtreeComplaintList
                              ? postsSubtreeComplaintList[feedItem.post.id]
                              : null,
                      isComplaintOpened: complaintsOpened[idx],
                  }),
              );

        if (hasAds(requestUser)) {
            const visibleItemsCount = items.filter(
                (item) =>
                    (item.type === "post" && !item.item?.deletedAt) ||
                    (item.type === "feeditem" && !item.item?.post?.deletedAt),
            ).length;

            ADSLOT_INDEXES.forEach((idx, i) => {
                if (visibleItemsCount >= idx + 2) {
                    items.splice(idx + i /* +i for previous inserts */, 0, {
                        type: `adslot${i + 1}` as PageItemType,
                    });
                }
            });
        }

        if (showHashtagsBox && hashtags.newest.length > 0) {
            items.splice(HASHTAGS_BOX_INDEX, 0, { type: "hashtags" });
        }

        return items;
    };

    private hashtagLinkCreate = (hashtag: string) => {
        return getBlogsFeedUrlWithHashtagLink(hashtag, "list");
    };
}

export default BlogFeedPostsWithPager;
